import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  StickyNote,
  Shield,
  Lock,
  Database,
  Cloud,
  CheckCircle2,
  Search,
  ArrowLeft,
  ChevronDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const FeatureCard = ({ icon: Icon, title, description, badge }) => (
  <div className="bg-white border border-gray-100 p-8 rounded-3xl hover:shadow-xl transition-all duration-300">
    <div className="flex justify-between items-start mb-6">
      <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
        <Icon size={24} />
      </div>
      {badge && (
        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-widest rounded-full">
          {badge}
        </span>
      )}
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600 leading-relaxed text-sm">{description}</p>
  </div>
);

const PlanCard = ({ title, subtitle, price, items, featured, t }) => (
  <div className={`relative bg-white border ${featured ? 'border-blue-500 shadow-2xl scale-105 z-10' : 'border-gray-100'} p-8 rounded-[2.5rem] flex flex-col`}>
    {featured && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
        Popular
      </div>
    )}
    <div className="mb-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-500 text-sm">{subtitle}</p>
    </div>
    <div className="mb-8">
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-extrabold text-[#141414]">${typeof price === 'string' ? price : price.monthly}</span>
        <span className="text-gray-500 text-sm">/ {t('common.month', { defaultValue: 'mes' })}</span>
      </div>
      {typeof price !== 'string' && (
        <p className="text-xs text-green-600 font-bold mt-2 italic">
          ${price.annual} / {t('common.year', { defaultValue: 'año' })} (save 20%)
        </p>
      )}
    </div>
    <div className="space-y-4 mb-10 flex-1">
      {items.map((item) => (
        <div key={item} className="flex items-start gap-3">
          <CheckCircle2 size={18} className="text-blue-500 mt-0.5" />
          <span className="text-sm text-gray-600">{item}</span>
        </div>
      ))}
    </div>
    <button className={`w-full py-4 rounded-2xl font-bold transition ${featured ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg shadow-blue-200' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}>
      {t('common.get_started', { defaultValue: 'Empezar ahora' })}
    </button>
  </div>
);

const NotexPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const mainFeatures = useMemo(() => {
    const items = t('notex.features', { returnObjects: true });
    const icons = [Shield, Lock, StickyNote];
    return items.map((item, idx) => ({ ...item, icon: icons[idx] }));
  }, [t]);

  const searchItems = useMemo(() => t('notex.search_items', { returnObjects: true }), [t]);
  const editorItems = useMemo(() => t('notex.editor_items', { returnObjects: true }), [t]);
  const driveItems = useMemo(() => t('notex.drive_items', { returnObjects: true }), [t]);
  const plans = useMemo(() => t('notex.plans', { returnObjects: true }), [t]);
  const faqs = useMemo(() => t('notex.faq', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter selection:bg-blue-100">
      <PageHeader />

      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <div className="text-center mb-24">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold bg-blue-50 text-blue-700 mb-6 border border-blue-100">
              {t('notex.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold text-[#141414] mb-6 tracking-tight leading-tight">
              {t('notex.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              {t('notex.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {t('notex.pills', { returnObjects: true }).map((pill) => (
                <span key={pill} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  {pill}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#planes"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-4 px-10 rounded-2xl transition shadow-xl"
              >
                {t('notex.cta_plans')}
              </a>
              <a
                href="#funciones"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-4 px-10 rounded-2xl transition"
              >
                {t('notex.cta_features')}
              </a>
            </div>
            <p className="mt-6 text-sm text-gray-500 font-medium italic">
              {t('notex.disclaimer')}
            </p>
          </div>

          <section id="funciones" className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32 scroll-mt-32">
            {mainFeatures.map((f) => (
              <FeatureCard
                key={f.title}
                icon={f.icon}
                title={f.title}
                description={f.desc}
              />
            ))}
          </section>

          <section className="mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">
                  {t('notex.search_badge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-6">
                  {t('notex.search_title')}
                </h2>
                <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                  {t('notex.search_desc')}
                </p>
                <div className="space-y-6">
                  {searchItems.map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-white border border-gray-100 rounded-lg flex items-center justify-center text-blue-500 shadow-sm">
                        <CheckCircle2 size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-[3rem] p-8 md:p-12 border border-gray-100">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                  <div className="p-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400/20" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400/20" />
                      <div className="w-3 h-3 rounded-full bg-green-400/20" />
                    </div>
                    <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-md border border-gray-100 shadow-sm">
                      <Search size={12} className="text-gray-400" />
                      <div className="h-2 w-24 bg-gray-100 rounded" />
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex gap-4 items-center p-3 hover:bg-gray-50 rounded-xl transition-colors">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-500">
                          <StickyNote size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="h-3 w-32 bg-gray-200 rounded mb-2" />
                          <div className="h-2 w-48 bg-gray-100 rounded" />
                        </div>
                        <ChevronDown size={16} className="text-gray-300" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-32 bg-[#141414] rounded-[3rem] p-8 md:p-20 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-blue-600/10 rounded-full blur-[100px] -mr-80 -mt-80" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-white/10 text-white/70 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">
                  {t('notex.editor_badge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  {t('notex.editor_title')}
                </h2>
                <p className="text-white/60 text-lg mb-10 leading-relaxed">
                  {t('notex.editor_desc')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {editorItems.map((item) => (
                    <span key={item} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-medium text-white/80">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/5 border border-white/10 p-8 rounded-[2rem] backdrop-blur-sm">
                  <Lock className="text-blue-400 mb-6" size={32} />
                  <h3 className="font-bold text-xl mb-4">{t('notex.security_title')}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{t('notex.security_desc')}</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-8 rounded-[2rem] backdrop-blur-sm">
                  <Database className="text-blue-400 mb-6" size={32} />
                  <h3 className="font-bold text-xl mb-4">{t('notex.backup_title')}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{t('notex.backup_desc')}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
                {driveItems.map((item) => (
                  <div key={item.title} className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm">
                    <CheckCircle2 size={20} className="text-blue-500 mb-4" />
                    <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="order-1 lg:order-2">
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">
                  {t('notex.drive_badge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-6">
                  {t('notex.drive_title')}
                </h2>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  {t('notex.drive_desc')}
                </p>
                <div className="bg-blue-50 border border-blue-100 p-6 rounded-2xl flex gap-4 items-start">
                  <Cloud className="text-blue-600 flex-shrink-0" size={24} />
                  <div className="text-sm text-blue-800 leading-relaxed">
                    {t('notex.backup_encrypted_desc')}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="planes" className="mb-32 scroll-mt-32">
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">
                {t('notex.planes_badge')}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#141414] mb-6">
                {t('notex.planes_title')}
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                {t('notex.planes_desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {plans.map((plan, idx) => (
                <PlanCard
                  key={plan.title}
                  title={plan.title}
                  subtitle={plan.subtitle}
                  price={plan.price}
                  items={plan.items}
                  featured={idx === 1}
                  t={t}
                />
              ))}
            </div>
          </section>

          <section className="max-w-3xl mx-auto mb-32">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('notex.faq_title')}</h2>
              <p className="text-gray-600">{t('notex.faq_desc')}</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-bold text-gray-900">{faq.q}</span>
                    <ChevronDown size={20} className={`text-gray-400 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="text-center bg-gray-50 rounded-[3rem] p-12 md:p-20 border border-gray-200">
            <h2 className="text-3xl md:text-5xl font-bold text-[#141414] mb-6 tracking-tight">
              {t('notex.footer_title')}
            </h2>
            <p className="text-gray-600 text-xl mb-12 max-w-2xl mx-auto">
              {t('notex.footer_desc')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/apps"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-4 px-12 rounded-2xl transition shadow-xl"
              >
                {t('notex.footer_btn')}
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default NotexPage;
